import React from 'react'
import {
    Box,
    Chip,
    Paper,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { PipelineStepInstance } from './pipelineBuilder.types'
import { isRequiredStepType } from './stepGuards'
import { CARD_H, CARD_W } from './flowLayout'
import { getStepDisplayLabel } from './stepDisplayUtils'
import { summarizeNode } from './transitionUtils'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        width: CARD_W,
        height: CARD_H,
        minHeight: CARD_H,
        boxSizing: 'border-box',
        padding: theme.spacing(1.5, 1.75),
        cursor: 'pointer',
        border: `2px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        transition: 'border-color 0.15s, box-shadow 0.15s',
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: theme.shadows[2],
        },
    },
    cardSelected: {
        borderColor: theme.palette.primary.main,
        boxShadow: theme.shadows[3],
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
    },
    title: {
        fontWeight: 700,
        fontSize: '0.9375rem',
    },
    summary: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        lineHeight: 1.4,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    requiredChip: {
        height: 18,
        fontSize: '0.6rem',
    },
}))

interface FlowNodeCardProps {
    step: PipelineStepInstance
    selected?: boolean
    onClick: () => void
}

export const FlowNodeCard: React.FC<FlowNodeCardProps> = ({
    step,
    selected = false,
    onClick,
}) => {
    const classes = useStyles()

    return (
        <Paper
            elevation={0}
            className={`${classes.card} ${
                selected ? classes.cardSelected : ''
            }`}
            onClick={onClick}
        >
            <Box className={classes.titleRow}>
                <Typography className={classes.title}>
                    {getStepDisplayLabel(step)}
                </Typography>
                {isRequiredStepType(step.type) && (
                    <Chip
                        size="small"
                        label="Required"
                        color="primary"
                        variant="outlined"
                        className={classes.requiredChip}
                    />
                )}
            </Box>
            <Typography className={classes.summary}>
                {summarizeNode(step)}
            </Typography>
        </Paper>
    )
}
